import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Sparkles, Loader2, ShieldCheck, Truck, Gem, CheckCircle2 } from 'lucide-react';
import { toast } from 'sonner';

import { API_BASE } from "../utils/apiBase";

const API = API_BASE;

const BENEFITS = [
  { icon: Gem, text: 'Activates and balances all 7 chakras of your body' },
  { icon: Sparkles, text: 'Reduces planetary dosha intensity (Shani, Rahu, Ketu)' },
  { icon: ShieldCheck, text: 'Creates a protective energy shield against negativity' },
  { icon: Truck, text: 'Free shipping anywhere in India · 5-7 working days' },
];

const BraceletCheckoutPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const refOrderId = searchParams.get('ref') || '';
  const promo = (searchParams.get('promo') || '').toUpperCase();

  // Display-only — server is the source of truth for billing.
  const PROMO_DISCOUNTS = { KK10: 110 };
  const discount = PROMO_DISCOUNTS[promo] || 0;
  const originalAmount = 1100;
  const finalAmount = originalAmount - discount;

  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({
    full_name: '', email: '', mobile: '',
    address_line1: '', address_line2: '', landmark: '',
    city: '', state: '', pincode: '',
  });

  useEffect(() => {
    document.title = '7-Chakra Bracelet · Karma Kundali';
    window.scrollTo(0, 0);
  }, []);

  const setField = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const validate = () => {
    if (form.full_name.trim().length < 2) return 'Please enter your full name.';
    if (!/^\S+@\S+\.\S+$/.test(form.email)) return 'Please enter a valid email.';
    const phoneDigits = form.mobile.replace(/\D/g, '').slice(-10);
    if (!/^[6-9]\d{9}$/.test(phoneDigits)) return 'Mobile must be a 10-digit number starting with 6/7/8/9.';
    if (form.address_line1.trim().length < 5) return 'Please enter a complete street address.';
    if (form.city.trim().length < 2) return 'Please enter your city.';
    if (form.state.trim().length < 2) return 'Please enter your state.';
    if (!/^\d{6}$/.test(form.pincode.trim())) return 'Pincode must be a 6-digit Indian PIN.';
    return null;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const err = validate();
    if (err) { toast.error(err); return; }
    setSubmitting(true);
    try {
      const r = await fetch(`${API}/api/bracelets/order`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...form,
          mobile: form.mobile.replace(/\D/g, '').slice(-10),
          parent_kundali_order_id: refOrderId || null,
          promo_code: promo || null,
        }),
      });
      const data = await r.json();
      if (!r.ok) throw new Error(data?.detail || 'Order failed');
      toast.success('Redirecting you to secure payment...');
      // Meta Pixel — InitiateCheckout for bracelet
      if (window.fbq) {
        window.fbq('track', 'InitiateCheckout', {
          content_name: '7-Chakra Bracelet',
          content_ids: ['kk-bracelet'],
          value: finalAmount,
          currency: 'INR',
          num_items: 1,
        });
      }
      setTimeout(() => { window.location.href = data.short_url; }, 600);
    } catch (e2) {
      toast.error(e2.message || 'Something went wrong, please try again.');
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="bracelet-checkout-page" className="min-h-screen bg-brand-cream relative">
      <div className="absolute inset-0 bg-mandala-watermark bg-repeat opacity-25 pointer-events-none" />
      <div className="relative max-w-5xl mx-auto px-4 sm:px-6 py-8 sm:py-12">
        {/* Header */}
        <div className="bg-gradient-to-br from-brand-maroon via-brand-maroon-dark to-[#3a050d] rounded-3xl shadow-premium border border-brand-gold/40 px-6 py-8 mb-8 text-center overflow-hidden relative">
          <div className="absolute inset-0 bg-mandala-large bg-center bg-no-repeat opacity-25 pointer-events-none" />
          <div className="relative">
            <Gem className="w-9 h-9 text-brand-gold-light mx-auto mb-2" />
            <p className="text-brand-gold-light/70 text-xs tracking-[0.3em] uppercase mb-1">Astrologer-Recommended</p>
            <h1 className="font-playfair font-bold text-3xl sm:text-4xl text-brand-gold-light leading-tight">
              7-Chakra Healing Bracelet
            </h1>
            <p className="text-white/70 text-sm mt-2 max-w-xl mx-auto">
              Energised with Vedic mantras for chakra balance, dosha shanti, and positive flow.
            </p>
            {promo === 'KK10' && (
              <div className="inline-block mt-4 bg-brand-gold text-brand-maroon-dark font-bold px-4 py-1.5 rounded-full text-sm" data-testid="bracelet-promo-banner">
                KK10 applied — ₹{discount} off · Pay only ₹{finalAmount}
              </div>
            )}
          </div>
        </div>

        <div className="grid lg:grid-cols-5 gap-6 lg:gap-8">
          {/* Left: Product details */}
          <div className="lg:col-span-2 space-y-5">
            <div className="bg-white rounded-2xl border-2 border-brand-gold/30 shadow-sm p-5 sm:p-6">
              <h2 className="font-playfair font-bold text-xl text-brand-maroon mb-3">What you get</h2>
              <ul className="space-y-3">
                {BENEFITS.map((b, i) => (
                  <li key={i} className="flex items-start gap-3 text-sm text-brand-text-dark">
                    <b.icon className="w-5 h-5 text-brand-maroon mt-0.5 shrink-0" />
                    <span>{b.text}</span>
                  </li>
                ))}
              </ul>
            </div>

            <div className="bg-white rounded-2xl border-2 border-brand-gold/30 shadow-sm p-5 sm:p-6">
              <div className="flex items-baseline justify-between mb-1">
                <span className="text-sm text-brand-text-soft">Total payable</span>
                <div className="text-right">
                  {discount > 0 && (
                    <span className="text-base text-brand-text-mute line-through mr-2">₹{originalAmount}</span>
                  )}
                  <span className="font-playfair font-bold text-3xl text-brand-maroon" data-testid="bracelet-amount">
                    ₹{finalAmount}
                  </span>
                </div>
              </div>
              <p className="text-xs text-brand-text-soft mt-2">
                Free shipping · COD not available · Dispatched within 24h of payment
              </p>
              <div className="mt-3 flex items-center gap-2 text-xs text-brand-text-soft">
                <ShieldCheck className="w-4 h-4 text-green-700" />
                Secure Razorpay payment · UPI / Cards / Net Banking
              </div>
            </div>

            {refOrderId && (
              <div className="bg-amber-50 border-2 border-amber-300 rounded-2xl p-4 text-xs text-amber-900" data-testid="bracelet-ref-banner">
                <div className="font-bold mb-1">Linked to your Kundali order</div>
                <code className="bg-white px-1.5 py-0.5 rounded">{refOrderId.slice(0, 8)}...</code> — Astro Avinash recommended this bracelet specifically based on your chart.
              </div>
            )}
          </div>

          {/* Right: Shipping form */}
          <form onSubmit={handleSubmit} className="lg:col-span-3 bg-white rounded-2xl border-2 border-brand-gold/30 shadow-sm p-5 sm:p-7 space-y-4">
            <h2 className="font-playfair font-bold text-xl text-brand-maroon">Shipping details</h2>
            <p className="text-xs text-brand-text-soft -mt-2">Bracelet is shipped to this address. Please double-check before payment.</p>

            <div className="grid sm:grid-cols-2 gap-4">
              <Field label="Full name *" testid="bracelet-name">
                <input
                  data-testid="bracelet-input-name"
                  required minLength={2}
                  value={form.full_name} onChange={setField('full_name')}
                  className={inputCls}
                />
              </Field>
              <Field label="Email *" testid="bracelet-email">
                <input
                  data-testid="bracelet-input-email"
                  type="email" required
                  value={form.email} onChange={setField('email')}
                  className={inputCls}
                />
              </Field>
            </div>

            <Field label="Mobile (10-digit) *" testid="bracelet-mobile">
              <div className="flex gap-2">
                <span className="px-3 py-3 bg-brand-cream border-2 border-brand-gold/30 rounded-xl text-brand-maroon font-semibold">+91</span>
                <input
                  data-testid="bracelet-input-mobile"
                  required inputMode="numeric" pattern="[0-9]*" maxLength={15}
                  value={form.mobile} onChange={setField('mobile')}
                  placeholder="98xxxxxxxx"
                  className={`${inputCls} flex-1`}
                />
              </div>
            </Field>

            <Field label="Address line 1 *" testid="bracelet-addr1">
              <input
                data-testid="bracelet-input-address1"
                required minLength={5} maxLength={200}
                value={form.address_line1} onChange={setField('address_line1')}
                placeholder="House / flat number, street"
                className={inputCls}
              />
            </Field>

            <Field label="Address line 2 (optional)" testid="bracelet-addr2">
              <input
                data-testid="bracelet-input-address2"
                maxLength={200}
                value={form.address_line2} onChange={setField('address_line2')}
                placeholder="Area, locality"
                className={inputCls}
              />
            </Field>

            <Field label="Landmark (optional)" testid="bracelet-landmark">
              <input
                data-testid="bracelet-input-landmark"
                maxLength={200}
                value={form.landmark} onChange={setField('landmark')}
                placeholder="e.g. opposite SBI bank"
                className={inputCls}
              />
            </Field>

            <div className="grid sm:grid-cols-3 gap-4">
              <Field label="City *" testid="bracelet-city">
                <input
                  data-testid="bracelet-input-city"
                  required minLength={2}
                  value={form.city} onChange={setField('city')}
                  className={inputCls}
                />
              </Field>
              <Field label="State *" testid="bracelet-state">
                <input
                  data-testid="bracelet-input-state"
                  required minLength={2}
                  value={form.state} onChange={setField('state')}
                  className={inputCls}
                />
              </Field>
              <Field label="Pincode *" testid="bracelet-pincode">
                <input
                  data-testid="bracelet-input-pincode"
                  required inputMode="numeric" pattern="[0-9]{6}" maxLength={6}
                  value={form.pincode} onChange={setField('pincode')}
                  placeholder="6-digit"
                  className={inputCls}
                />
              </Field>
            </div>

            <button
              type="submit"
              disabled={submitting}
              data-testid="bracelet-submit-btn"
              className="w-full inline-flex items-center justify-center gap-2 bg-brand-maroon hover:bg-brand-maroon-dark disabled:opacity-60 text-brand-gold-light font-bold px-6 py-4 rounded-full shadow-maroon-soft hover:-translate-y-0.5 transition-all text-base"
            >
              {submitting ? (
                <><Loader2 className="w-5 h-5 animate-spin" /> Creating secure link…</>
              ) : (
                <>Continue to Pay ₹{finalAmount}</>
              )}
            </button>
            <p className="text-center text-xs text-brand-text-soft">
              By continuing you agree to our <button type="button" onClick={() => navigate('/terms-conditions')} className="underline hover:text-brand-maroon">Terms</button> and <button type="button" onClick={() => navigate('/refund-policy')} className="underline hover:text-brand-maroon">Refund Policy</button>.
            </p>
          </form>
        </div>
      </div>
    </div>
  );
};

const inputCls =
  'w-full px-4 py-3 rounded-xl bg-white border-2 border-brand-gold/30 focus:border-brand-gold focus:outline-none text-sm text-brand-text-dark';

const Field = ({ label, children, testid }) => (
  <label className="block" data-testid={testid}>
    <span className="block text-xs font-semibold text-brand-text-dark mb-1.5 tracking-wider uppercase">{label}</span>
    {children}
  </label>
);

export const BraceletSuccessPage = () => {
  const [searchParams] = useSearchParams();
  const orderId = searchParams.get('order_id') || '';

  useEffect(() => {
    document.title = 'Bracelet Order Confirmed · Karma Kundali';
    if (window.fbq) {
      window.fbq('track', 'Purchase', {
        content_name: '7-Chakra Bracelet',
        content_ids: ['kk-bracelet'],
        value: 1100,
        currency: 'INR',
      });
    }
  }, []);

  return (
    <div data-testid="bracelet-success-page" className="min-h-screen bg-brand-cream flex items-center justify-center px-4">
      <div className="max-w-xl w-full bg-white rounded-3xl border-2 border-brand-gold/40 shadow-premium p-8 text-center">
        <CheckCircle2 className="w-16 h-16 text-green-700 mx-auto mb-3" />
        <h1 className="font-playfair font-bold text-3xl text-brand-maroon mb-2">Order confirmed</h1>
        <p className="text-brand-text-soft text-sm mb-4">
          Your 7-Chakra Bracelet is on its way. Astro Avinash will personally bless and dispatch it within 24 hours. You'll receive a tracking email at the address on file.
        </p>
        {orderId && (
          <div className="text-xs bg-brand-cream rounded-lg border border-brand-gold/30 p-3 mb-4">
            <span className="text-brand-text-soft">Order ID:</span>{' '}
            <code className="font-mono text-brand-maroon" data-testid="bracelet-success-order-id">{orderId}</code>
          </div>
        )}
        <div className="text-xs text-brand-text-soft">
          For any queries, write to <a href="mailto:karmakundali2@gmail.com" className="text-brand-maroon underline">karmakundali2@gmail.com</a>
        </div>
      </div>
    </div>
  );
};

export default BraceletCheckoutPage;
